import * as React from "react";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Modal as MuiModal,
  TextField,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
} from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers/icons";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
  KeyboardTimePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { isValid, isSameDay } from "date-fns";
import { useFlash } from "../FlashContext";
import { getHeaders } from "../utils/HeaderHelper";
import { AuthenticationContext } from "../Context";

interface NewOutreachModalProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  onSaveSuccess: () => void;
}

type FormErrors = Partial<Record<"date" | "time" | "contactMethod" | "category" | "outcome", string>>;

const NOTES_MAX_LENGTH = 1000;

const fieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#EFE9E7",
    borderRadius: "5px",
  },
  "& fieldset": { border: "none" },
};

const pickerInputStyle: React.CSSProperties = {
  color: "#333",
  fontFamily: "QuicksandBold",
  fontSize: "14px",
  fontWeight: 500,
  padding: "0 4px",
};

const pickerInputContainerStyle: React.CSSProperties = {
  backgroundColor: "#EFE9E7",
  borderRadius: "5px",
  height: "44px",
  padding: "0 12px",
};

const pickerStyle: React.CSSProperties = {
  margin: 0,
  width: "100%",
};

const NewOutreachModal: React.FC<NewOutreachModalProps> = ({
  open,
  onClose,
  patientId,
  onSaveSuccess,
}) => {
  const { showToast } = useFlash();
  const authenticationSetting = React.useContext(AuthenticationContext);

  const [outreachDate, setOutreachDate] = React.useState<Date | null>(new Date());
  const [outreachTime, setOutreachTime] = React.useState<Date | null>(new Date());
  const [showDatePicker, setShowDatePicker] = React.useState(false);
  const [showTimePicker, setShowTimePicker] = React.useState(false);
  const [contactMethod, setContactMethod] = React.useState<string>("");
  const [category, setCategory] = React.useState<string>("");
  const [outcome, setOutcome] = React.useState<string>("");
  const [notes, setNotes] = React.useState<string>("");
  const [errors, setErrors] = React.useState<FormErrors>({});
  const [loading, setLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!open) {
      setOutreachDate(new Date());
      setOutreachTime(new Date());
      setShowDatePicker(false);
      setShowTimePicker(false);
      setContactMethod("");
      setCategory("");
      setOutcome("");
      setNotes("");
      setErrors({});
      setLoading(false);
    }
  }, [open]);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const renderDay = (day, selectedDate, isInCurrentMonth, dayComponent) => {
    const today = new Date();
    const isToday = isSameDay(day, today) && !isSameDay(selectedDate, today);
    return React.cloneElement(dayComponent, {
      style: { color: isToday ? "#ff890a" : undefined },
    });
  };

  const handleSubmit = async () => {
    const newErrors: FormErrors = {};
    if (!outreachDate) newErrors.date = "Please select a date.";
    if (!outreachTime) newErrors.time = "Please select a time.";
    if (!contactMethod) newErrors.contactMethod = "Please select a Contact Method.";
    if (!category) newErrors.category = "Please select a Category.";
    if (!outcome) newErrors.outcome = "Please select an Outcome.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    const combinedDateTime = new Date(outreachDate!);
    combinedDateTime.setHours(outreachTime!.getHours(), outreachTime!.getMinutes(), 0, 0);

    const payload = {
      outreach_record: {
        date_time_of_outreach: combinedDateTime.toISOString(),
        contact_method: contactMethod,
        category: category,
        outcome: outcome,
        notes: notes.trim() || null,
      },
    };

    try {
      const response = await fetch(`/patients/${patientId}/outreach_records`, {
        method: "POST",
        headers: getHeaders(authenticationSetting.csrfToken),
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (!response.ok || result.success === false) {
        showToast({
          title: "Error",
          text: result.error || result.message || "Failed to save outreach record.",
          type: "error",
        });
        return;
      }

      showToast({ title: "Success", text: "Outreach record created successfully.", type: "success" });
      onSaveSuccess();
      onClose();
    } catch (error: any) {
      showToast({ title: "Error", text: "Failed to save outreach record.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <MuiModal open={open} className="modal-primary" onClose={handleClose}>
      <div
        className="paper"
        style={{ width: 450, maxHeight: "90vh", display: "flex", flexDirection: "column" }}
      >
        <div
          className="paperInner"
          style={{ display: "flex", flexDirection: "column", flexGrow: 1, overflow: "hidden" }}
        >
          <Grid
            container
            sx={{ flexGrow: 1, display: "flex", flexDirection: "column", flexWrap: "nowrap", overflow: "hidden" }}
          >
            {/* Header */}
            <Grid item xs={12} sx={{ flexShrink: 0 }}>
              <div className="main-header">
                <span>Record Outreach</span>
                <span id="dismiss-button" onClick={handleClose}>
                  <ClearIcon sx={{ width: 30, height: 30 }} />
                </span>
              </div>
            </Grid>

            {/* Scrollable Form Body */}
            <Grid
              item
              xs={12}
              sx={{
                paddingLeft: "40px",
                paddingRight: "40px",
                paddingBottom: "24px",
                paddingTop: "0px",
                display: "flex",
                flexDirection: "column",
                rowGap: "20px",
                overflowY: "auto",
                flexGrow: 1,
                "& .field-container": { marginLeft: 0 },
                "& .field-container .the-field": { width: "100%" },
              }}
            >

              {/* Date */}
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="outreach-date" className="field-label">Date*</InputLabel>
                </Grid>
                <Grid
                  container
                  xs={12}
                  className="field-container"
                  alignItems="center"
                  sx={{ marginLeft: 0, gap: "8px", flexWrap: "nowrap" }}
                >
                  <Box sx={{ flex: 1, maxHeight: 48 }}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <KeyboardDatePicker
                        disableToolbar
                        autoOk
                        variant="inline"
                        format="MM/dd/yyyy"
                        margin="normal"
                        id="outreach-date"
                        renderDay={renderDay}
                        value={outreachDate}
                        open={showDatePicker}
                        onOpen={() => !loading && setShowDatePicker(true)}
                        onClose={() => setShowDatePicker(false)}
                        onChange={(date) => {
                          if (date instanceof Date && isValid(date)) {
                            setOutreachDate(date);
                            setShowDatePicker(false);
                            setErrors((prev) => ({ ...prev, date: undefined }));
                          }
                        }}
                        disabled={loading}
                        KeyboardButtonProps={{ style: { display: "none" } }}
                        InputProps={{
                          disableUnderline: true,
                          style: pickerInputContainerStyle,
                          inputProps: { placeholder: "mm/dd/yyyy", style: pickerInputStyle },
                        }}
                        style={pickerStyle}
                      />
                    </MuiPickersUtilsProvider>
                  </Box>
                  <Link
                    className="invite-app-user-link"
                    onClick={() => !loading && setShowDatePicker(true)}
                    sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                  >
                    <CalendarTodayIcon sx={{ width: 24, height: 24, color: "#888" }} />
                  </Link>
                </Grid>
                {errors.date && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, pl: 0.5 }}>
                    {errors.date}
                  </Typography>
                )}
              </Grid>

              {/* Time */}
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="outreach-time" className="field-label">Time*</InputLabel>
                </Grid>
                <Grid
                  container
                  xs={12}
                  className="field-container"
                  alignItems="center"
                  sx={{ marginLeft: 0, gap: "8px", flexWrap: "nowrap" }}
                >
                  <Box sx={{ flex: 1, maxHeight: 48 }}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <KeyboardTimePicker
                        margin="normal"
                        id="outreach-time"
                        value={outreachTime}
                        open={showTimePicker}
                        onOpen={() => !loading && setShowTimePicker(true)}
                        onClose={() => setShowTimePicker(false)}
                        onChange={(time) => {
                          setOutreachTime(time as Date | null);
                          setErrors((prev) => ({ ...prev, time: undefined }));
                        }}
                        disabled={loading}
                        KeyboardButtonProps={{ style: { display: "none" } }}
                        InputProps={{
                          disableUnderline: true,
                          style: pickerInputContainerStyle,
                          inputProps: { placeholder: "hh:mm am", style: pickerInputStyle },
                        }}
                        style={pickerStyle}
                      />
                    </MuiPickersUtilsProvider>
                  </Box>
                  <Link
                    className="invite-app-user-link"
                    onClick={() => !loading && setShowTimePicker(true)}
                    sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                  >
                    <AccessTimeIcon sx={{ width: 28, height: 28, color: "#888" }} />
                  </Link>
                </Grid>
                {errors.time && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, pl: 0.5 }}>
                    {errors.time}
                  </Typography>
                )}
              </Grid>

              {/* Contact Method */}
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="contactMethod" className="field-label">Contact Method*</InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="contactMethod"
                    select
                    fullWidth
                    size="small"
                    value={contactMethod}
                    className="the-field"
                    required
                    variant="outlined"
                    disabled={loading}
                    onChange={(e) => setContactMethod(e.target.value)}
                    sx={fieldSx}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value="" disabled>Select Contact Method</MenuItem>
                    {["Text", "Phone", "Email", "Mail"].map((method) => (
                      <MenuItem key={method} value={method}>{method}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {errors.contactMethod && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.contactMethod}
                  </Typography>
                )}
              </Grid>

              {/* Category */}
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="category" className="field-label">Event Category*</InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="category"
                    select
                    fullWidth
                    size="small"
                    value={category}
                    className="the-field"
                    required
                    variant="outlined"
                    disabled={loading}
                    onChange={(e) => setCategory(e.target.value)}
                    sx={fieldSx}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value="" disabled>Select Event Category</MenuItem>
                    {["Contact Attempt", "Enrollment", "Informative"].map((cat) => (
                      <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {errors.category && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.category}
                  </Typography>
                )}
              </Grid>

              {/* Outcome */}
              <Grid container>
                <Grid item xs={12}>
                  <FormLabel className="field-label" sx={{ color: "#000", mb: 0.5, display: "block" }}>
                    Outcome*
                  </FormLabel>
                </Grid>
                <Grid item xs={12} className="field-container" sx={{ mt: "4px" }}>
                  <RadioGroup
                    row
                    name="outreach-outcome"
                    value={outcome}
                    onChange={(e) => setOutcome(e.target.value)}
                    sx={{
                      display: "flex",
                      gap: "12px",
                      "& .MuiFormControlLabel-root": { marginRight: 0 },
                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.95rem",
                        color: "#666",
                        fontFamily: "inherit",
                        marginLeft: "-4px",
                      },
                    }}
                  >
                    <FormControlLabel
                      value="Successful"
                      label="Successful"
                      disabled={loading}
                      control={<Radio size="small" sx={{ color: "#ccc", "&.Mui-checked": { color: "orange" } }} />}
                    />
                    <FormControlLabel
                      value="Unsuccessful"
                      label="Unsuccessful"
                      disabled={loading}
                      control={<Radio size="small" sx={{ color: "#ccc", "&.Mui-checked": { color: "orange" } }} />}
                    />
                  </RadioGroup>
                </Grid>
                {errors.outcome && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.outcome}
                  </Typography>
                )}
              </Grid>

              {/* Notes (Optional) */}
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="notes" className="field-label">Notes</InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="notes"
                    fullWidth
                    size="small"
                    value={notes}
                    className="the-field"
                    variant="outlined"
                    placeholder="Description"
                    disabled={loading}
                    multiline
                    minRows={4}
                    inputProps={{ maxLength: NOTES_MAX_LENGTH }}
                    onChange={(e) => setNotes(e.target.value)}
                    sx={{
                      ...fieldSx,
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#EFE9E7",
                        borderRadius: "5px",
                        padding: "0 !important",
                      },
                      "& .MuiInputBase-inputMultiline": { padding: "10.5px 14px" },
                    }}
                  />
                </Grid>
              </Grid>

            </Grid>
          </Grid>

          {/* Footer */}
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            className="modal-footer"
            sx={{ flexShrink: 0 }}
          >
            <Grid item xs={12} className="confirm-btn-container">
              <div onClick={handleClose} className="cancel-link">Cancel</div>
              <Button
                onClick={handleSubmit}
                className="confirm-btn"
                style={{ width: 156 }}
                disabled={loading}
              >
                Add Outreach
              </Button>
            </Grid>
          </Grid>

        </div>
        <Backdrop open={loading} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress />
        </Backdrop>
      </div>
    </MuiModal>
  );
};

export default NewOutreachModal;
